/*
 * NextGenn engine · the ritual builder (sections/ngs-ritual.liquid).
 *
 * The section arrives from Liquid complete and empty: five rows of real
 * buttons, a summary reading zero. This element only keeps the choice, does
 * the arithmetic and writes the answer back, so the page is whole before the
 * script lands and there is nothing to hydrate.
 *
 * THE BUNDLE RULE is the coded demo's `priceRitual` (Demo Websites/Dental ·
 * src/niches/store/steps.ts), to the dollar:
 *
 *   · three bottles or more, 10% off; all five, 15%; fewer, nothing
 *   · total = floor(sum × (1 − pct / 100)), DOWN to the whole dollar, because
 *     a shop that rounds a discount up has quietly taken back part of it
 *   · a ritual that is exactly a set's bottles costs what the SET costs. Without
 *     that clause the Morning's five built by hand come to $151 while the same
 *     five sold as a set cost $150, and a customer who noticed would be right
 *     to stop trusting every other number on the page.
 *
 * Money is kept in cents, as Shopify keeps it, and the floor is taken on whole
 * numbers (sum × (100 − pct) ÷ 10000), so no total is ever a float's rounding
 * error away from the demo's. Checked against the demo's own formula over all
 * 432 rituals the twelve can make: no differences.
 */
if (!customElements.get('ngs-ritual')) {
  customElements.define(
    'ngs-ritual',
    class NGSRitual extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        var source = this.querySelector('[data-ritual-config]');
        try {
          this.config = JSON.parse(source ? source.textContent : '{}');
        } catch (error) {
          this.config = {};
        }
        this.labels = this.config.labels || {};
        this.words = this.config.words || {};
        this.rule = this.config.rule || { three: 0, five: 0 };
        this.sets = (this.config.sets || [])
          .map(function (set) {
            return {
              name: set.name || '',
              price: Number(set.price) || 0,
              keys: String(set.keys || '')
                .split(',')
                .map(function (key) {
                  return key.trim();
                })
                .filter(Boolean),
            };
          })
          .filter(function (set) {
            return set.keys.length > 0 && set.price > 0;
          });
        this.symbol = this.dataset.symbol || '';

        /* The steps are whichever rows Liquid drew, in the order it drew them:
           a step with no bottles has no row and is not counted. */
        this.rows = Array.from(this.querySelectorAll('[data-ritual-row]'));
        this.steps = this.rows.map(function (row) {
          return row.dataset.ritualRow;
        });
        this.chosen = {};
        this.shown = 0;
        this.busy = false;

        this.parts = {
          counts: Array.from(this.querySelectorAll('[data-ritual-count]')),
          empty: this.querySelector('[data-ritual-empty]'),
          list: this.querySelector('[data-ritual-list]'),
          sums: this.querySelector('[data-ritual-sums]'),
          sum: this.querySelector('[data-ritual-sum]'),
          savingRow: this.querySelector('[data-ritual-saving-row]'),
          saving: this.querySelector('[data-ritual-saving]'),
          barSaving: this.querySelector('[data-ritual-bar-saving]'),
          set: this.querySelector('[data-ritual-set]'),
          total: this.querySelector('[data-ritual-total]'),
          nudge: this.querySelector('[data-ritual-nudge]'),
          add: this.querySelector('[data-ritual-add]'),
          wa: this.querySelector('[data-ritual-wa]'),
          note: this.querySelector('[data-ritual-note]'),
          status: this.querySelector('[data-ritual-status]'),
          live: this.querySelector('[data-ritual-live]'),
        };

        /* The chips' prices are rewritten through the same formatter as the
           summary, so the two can never disagree about a trailing ".00". */
        this.querySelectorAll('[data-ritual-chip]').forEach(
          function (chip) {
            var label = chip.querySelector('[data-ritual-price]');
            if (label) label.textContent = this.money(this.cents(chip));
          }.bind(this)
        );

        this.addEventListener('click', this.onClick.bind(this));
        /* In the theme editor, choosing a bottle in the sidebar brings its chip
           into view: on a phone-width preview it may be off the end of its row. */
        this.addEventListener('shopify:block:select', function (event) {
          if (event.target && event.target.scrollIntoView) {
            event.target.scrollIntoView({ block: 'nearest', inline: 'center' });
          }
        });

        this.render(false);
      }

      disconnectedCallback() {
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      /* ── the choice ─────────────────────────────────────────────────────── */

      onClick(event) {
        var chip = event.target.closest('[data-ritual-chip]');
        if (chip && this.contains(chip)) {
          var step = chip.dataset.step;
          /* One bottle a step. The chosen one, tapped again, is a skip. */
          this.chosen[step] = this.chosen[step] === chip ? null : chip;
          this.render(true);
          if (this.chosen[step]) this.reveal(chip);
          return;
        }
        var skip = event.target.closest('[data-ritual-skip]');
        if (skip && this.contains(skip)) {
          var row = skip.closest('[data-ritual-row]');
          var was = this.chosen[skip.dataset.ritualSkip];
          this.chosen[skip.dataset.ritualSkip] = null;
          this.render(true);
          /* The skip button has just hidden itself: hand focus to the bottle
             that was chosen rather than dropping it on the page. */
          if (was) was.focus();
          else if (row) row.querySelector('[data-ritual-chip]').focus();
          return;
        }
        var add = event.target.closest('[data-ritual-add]');
        if (add && this.contains(add)) this.addToBag();
      }

      /* On a phone the third bottle of a row peeks in from the edge. Tapping the
         half of it that shows brings the whole chip into its row, sideways only:
         the page itself never moves under the customer's thumb. */
      reveal(chip) {
        var rail = chip.closest('ul');
        if (!rail || rail.scrollWidth <= rail.clientWidth) return;
        var frame = rail.getBoundingClientRect();
        var box = chip.getBoundingClientRect();
        var gutter = 15;
        var shift = box.left < frame.left + gutter ? box.left - frame.left - gutter : box.right > frame.right - gutter ? box.right - frame.right + gutter : 0;
        if (!shift) return;
        var calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        rail.scrollBy({ left: shift, behavior: calm ? 'auto' : 'smooth' });
      }

      picked() {
        return this.steps
          .map(
            function (step) {
              return this.chosen[step];
            }.bind(this)
          )
          .filter(Boolean);
      }

      cents(chip) {
        return Math.max(0, Math.round(Number(chip.dataset.price)) || 0);
      }

      /* ── the bundle rule ────────────────────────────────────────────────── */

      price() {
        var picked = this.picked();
        var sum = picked.reduce(
          function (total, chip) {
            return total + this.cents(chip);
          }.bind(this),
          0
        );
        var keys = picked.map(function (chip) {
          return chip.dataset.key;
        });

        /* Exactly a set's bottles: no more, no fewer, any order. Four of the
           Morning's five is not the Morning, it is four bottles at the rule.
           A set is never allowed to cost MORE than its bottles, which can only
           happen if a real product's price is edited below the set's. */
        var set = this.sets.find(function (candidate) {
          return (
            candidate.keys.length === picked.length &&
            candidate.price <= sum &&
            candidate.keys.every(function (key) {
              return keys.indexOf(key) !== -1;
            })
          );
        });
        if (set) return { count: picked.length, sum: sum, total: set.price, saving: sum - set.price, percent: this.rule.five, set: set };

        var percent = picked.length >= 5 ? this.rule.five : picked.length >= 3 ? this.rule.three : 0;
        var total = percent === 0 ? sum : Math.floor((sum * (100 - percent)) / 10000) * 100;
        return { count: picked.length, sum: sum, total: total, saving: sum - total, percent: percent, set: null };
      }

      /* One or two bottles from the next saving, and only then: at none chosen
         it would be a pitch, and at five there is nothing further to say. */
      nudge(price) {
        if (price.set) return '';
        var target = price.count < 3 && this.rule.three > 0 ? 3 : price.count < 5 && this.rule.five > price.percent ? 5 : 0;
        if (!target || target > this.steps.length) return '';
        var away = target - price.count;
        if (away !== 1 && away !== 2) return '';
        if (price.percent > 0) return away === 1 ? this.labels.nudgeOneAgain : this.labels.nudgeTwoAgain;
        return away === 1 ? this.labels.nudgeOne : this.labels.nudgeTwo;
      }

      /* ── the words ──────────────────────────────────────────────────────── */

      money(cents) {
        var whole = cents % 100 === 0;
        return (
          this.symbol +
          (cents / 100).toLocaleString('en-US', {
            minimumFractionDigits: whole ? 0 : 2,
            maximumFractionDigits: 2,
          })
        );
      }

      minus(cents) {
        return '−' + this.money(cents);
      }

      countLabel(count) {
        return String(this.labels.count || '')
          .replace('[n]', count)
          .replace('[total]', this.steps.length);
      }

      message(price) {
        var lines = [this.labels.waIntro, ''];
        this.steps.forEach(
          function (step) {
            var chip = this.chosen[step];
            if (chip) lines.push((this.words[step] || step) + ': ' + chip.dataset.title + ' · ' + this.money(this.cents(chip)));
          }.bind(this)
        );
        lines.push('');
        if (price.saving > 0) {
          lines.push(this.labels.sum + ': ' + this.money(price.sum));
          lines.push(this.labels.saving + ': ' + this.minus(price.saving));
        }
        lines.push(this.labels.total + ': ' + this.money(price.total));
        return lines.join('\n').trim();
      }

      /* ── writing it back ────────────────────────────────────────────────── */

      render(moved) {
        var parts = this.parts;
        var price = this.price();
        var picked = this.picked();
        this.dataset.count = price.count;

        this.rows.forEach(
          function (row) {
            var choice = this.chosen[row.dataset.ritualRow] || null;
            row.querySelectorAll('[data-ritual-chip]').forEach(function (chip) {
              chip.setAttribute('aria-pressed', chip === choice ? 'true' : 'false');
            });
            var hint = row.querySelector('[data-ritual-hint]');
            var skip = row.querySelector('[data-ritual-skip]');
            if (hint) hint.hidden = Boolean(choice);
            if (skip) skip.hidden = !choice;
          }.bind(this)
        );

        parts.counts.forEach(
          function (node) {
            node.textContent = this.countLabel(price.count);
          }.bind(this)
        );

        if (parts.list) {
          parts.list.textContent = '';
          this.steps.forEach(
            function (step) {
              var chip = this.chosen[step];
              if (!chip) return;
              var item = document.createElement('li');
              item.className = 'ngs-ritual__item';
              [
                ['ngs-ritual__item-step', this.words[step] || step],
                ['ngs-ritual__item-name', chip.dataset.title],
                ['ngs-ritual__item-price', this.money(this.cents(chip))],
              ].forEach(function (pair, index) {
                var span = document.createElement('span');
                span.className = pair[0];
                span.textContent = pair[1];
                if (index === 2) span.dir = 'ltr';
                item.appendChild(span);
              });
              parts.list.appendChild(item);
            }.bind(this)
          );
          parts.list.hidden = price.count === 0;
        }
        if (parts.empty) parts.empty.hidden = price.count > 0;

        if (parts.sums) parts.sums.hidden = price.count === 0;
        if (parts.sum) parts.sum.textContent = this.money(price.sum);
        if (parts.savingRow) parts.savingRow.hidden = price.saving <= 0;
        if (parts.saving) parts.saving.textContent = this.minus(price.saving);
        if (parts.barSaving) {
          parts.barSaving.textContent = this.minus(price.saving);
          parts.barSaving.hidden = price.saving <= 0;
        }
        if (parts.set) {
          parts.set.textContent = price.set ? String(this.labels.setNote || '').replace('[set]', price.set.name) : '';
          parts.set.hidden = !price.set || !parts.set.textContent;
        }

        var nudge = this.nudge(price);
        if (parts.nudge) {
          parts.nudge.textContent = nudge || '';
          parts.nudge.hidden = !nudge;
        }

        this.count(price.total, moved);
        this.action(price, picked);

        /* One sentence for a screen reader, once per change, rather than the
           total read out at every frame of its animation. */
        if (parts.live && moved) {
          parts.live.textContent = [
            this.countLabel(price.count),
            this.labels.total + ' ' + this.money(price.total),
            price.saving > 0 ? this.labels.saving + ' ' + this.minus(price.saving) : '',
            nudge || '',
          ]
            .filter(Boolean)
            .join('. ');
        }
      }

      /* The button is whichever the chosen bottles allow: the bag when every one
         of them is a product that can be bought, WhatsApp when one is not, and
         a plain note when there is no number to send it to either. */
      action(price, picked) {
        var parts = this.parts;
        var none = picked.length === 0;
        var every = function (chip) {
          return Boolean(chip.dataset.variant);
        };
        var pool = none ? Array.from(this.querySelectorAll('[data-ritual-chip]')) : picked;
        var bag = pool.length > 0 && pool.every(every);
        var wa = !bag && Boolean(this.config.wa);

        this.say('');
        if (parts.note) parts.note.hidden = bag || wa;

        if (wa && !none) {
          parts.wa.href = 'https://wa.me/' + this.config.wa + '?text=' + encodeURIComponent(this.message(price));
          parts.wa.hidden = false;
          parts.add.hidden = true;
          return;
        }
        if (parts.wa) parts.wa.hidden = true;
        parts.add.hidden = false;
        parts.add.textContent = bag ? this.labels.add : this.labels.whatsapp;
        parts.add.disabled = none || !bag;
        parts.add.dataset.mode = bag ? 'bag' : 'wait';
      }

      /* The total counts to its new value, in whole amounts so it never flickers
         through cents, and only for someone who has not asked for less motion. */
      count(to, moved) {
        var node = this.parts.total;
        if (!node) return;
        if (this.frame) cancelAnimationFrame(this.frame);
        var from = this.shown;
        var still = !moved || from === to || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (still) {
          this.shown = to;
          node.textContent = this.money(to);
          return;
        }
        var began = performance.now();
        var tick = function (now) {
          var t = Math.min(1, (now - began) / 420);
          var eased = 1 - Math.pow(1 - t, 3);
          this.shown = t === 1 ? to : Math.round((from + (to - from) * eased) / 100) * 100;
          node.textContent = this.money(this.shown);
          if (t < 1) this.frame = requestAnimationFrame(tick);
        }.bind(this);
        this.frame = requestAnimationFrame(tick);
      }

      /* ── the bag ────────────────────────────────────────────────────────── */

      addToBag() {
        var button = this.parts.add;
        if (this.busy || button.disabled || button.dataset.mode !== 'bag') return;
        var items = this.picked().map(function (chip) {
          return { id: chip.dataset.variant, quantity: 1 };
        });
        if (!items.length) return;
        if (!window.NGSCart) {
          this.say(this.labels.error);
          return;
        }

        this.busy = true;
        this.say('');
        button.setAttribute('aria-disabled', 'true');
        button.textContent = this.labels.adding || this.labels.add;

        /* Every bottle in ONE request: the ritual is in the bag or it is not. */
        window.NGSCart.add(items)
          .catch(
            function (error) {
              /* Shopify's own sentence when it wrote one ("All 2 The Serum are
                 in your cart."), ours when the connection simply dropped. */
              this.say(error && error.response ? error.message : this.labels.error);
            }.bind(this)
          )
          .finally(
            function () {
              this.busy = false;
              button.removeAttribute('aria-disabled');
              button.textContent = this.labels.add;
            }.bind(this)
          );
      }

      say(words) {
        var node = this.parts.status;
        if (!node) return;
        node.textContent = words || '';
        node.hidden = !words;
      }
    }
  );
}
